import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { pregnancyWeeks } from '../mockData';
import { ChevronLeft, ChevronRight, Calendar, Heart, Activity, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import RegisterPopup from '../components/RegisterPopup';


const PregnancyTracker = () => {
  const navigate = useNavigate();
  const { isLoggedIn, currentUser } = useAuth();
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Try to get user's pregnancy week from various sources
    const savedWeek = localStorage.getItem("pregnancy_week");
    if (savedWeek) return parseInt(savedWeek);
    
    const currentUserWeek = localStorage.getItem("currentUser");
    if (currentUserWeek) {
      try {
        const user = JSON.parse(currentUserWeek);
        if (user.currentWeek) return user.currentWeek;
      } catch {}
    }
    
    // Default to 24 if no user week found
    return 24;
  });
  
  const currentWeekData = pregnancyWeeks.find(w => w.week === selectedWeek) || pregnancyWeeks[0];

  useEffect(() => {
    if (!isLoggedIn) {
      setShowRegisterPopup(true);
    }
  }, [isLoggedIn]);

  // Find closest available week from the data
  const findClosestWeek = (targetWeek) => {
    const availableWeeks = pregnancyWeeks.map(w => w.week);
    if (availableWeeks.includes(targetWeek)) return targetWeek;
    
    // Find closest week
    let closest = availableWeeks[0];
    let minDiff = Math.abs(targetWeek - closest);
    
    for (const week of availableWeeks) {
      const diff = Math.abs(targetWeek - week);
      if (diff < minDiff) {
        minDiff = diff;
        closest = week;
      }
    }
    return closest;
  };

  // Set initial week based on user's pregnancy week
  useEffect(() => {
    const userWeek = currentUser?.currentWeek || localStorage.getItem("pregnancy_week");
    if (userWeek) {
      const targetWeek = parseInt(userWeek);
      const closestWeek = findClosestWeek(targetWeek);
      setSelectedWeek(closestWeek);
    }
  }, [currentUser]);

  const handleWeekChange = (direction) => {
    const currentIndex = pregnancyWeeks.findIndex(w => w.week === selectedWeek);
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedWeek(pregnancyWeeks[currentIndex - 1].week);
    } else if (direction === 'next' && currentIndex < pregnancyWeeks.length - 1) {
      setSelectedWeek(pregnancyWeeks[currentIndex + 1].week);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              Theo dõi thai kỳ
            </span>
          </h1>
          <p className="text-2xl text-gray-600">
            Khám phá sự phát triển của bé theo từng tuần
          </p>
        </div>

        {/* Week Selector */}
        <Card className="mb-8 border-pink-100 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleWeekChange('prev')}
                disabled={selectedWeek === 4}
                className="hover:bg-pink-50 hover:border-pink-300 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="text-center">
                <div className="text-5xl font-bold bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent mb-2">
                  Tuần {selectedWeek}
                </div>
                <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-200 text-sm px-4 py-1">
                  <Calendar className="w-4 h-4 mr-1" />
                  {selectedWeek <= 12 ? 'Tam cá nguyệt 1' : selectedWeek <= 27 ? 'Tam cá nguyệt 2' : 'Tam cá nguyệt 3'}
                </Badge>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => handleWeekChange('next')}
                disabled={selectedWeek === 40}
                className="hover:bg-pink-50 hover:border-pink-300 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Week Timeline */}
            <div className="mt-6 px-8">
              <div className="relative">
                <div className="absolute top-1/2 left-0 right-0 h-2 bg-pink-100 rounded-full" />
                <div 
                  className="absolute top-1/2 left-0 h-2 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full transition-all duration-500"
                  style={{ width: `${(selectedWeek / 40) * 100}%` }}
                />
                <div className="relative flex justify-between">
                  {[4, 12, 20, 28, 36, 40].map((week) => (
                    <button
                      key={week}
                      onClick={() => setSelectedWeek(week)}
                      className={`w-8 h-8 rounded-full border-4 transition-all duration-300 ${
                        selectedWeek >= week
                          ? 'bg-pink-500 border-white shadow-lg scale-110'
                          : 'bg-white border-pink-200'
                      } hover:scale-125`}
                    >
                      <span className="text-xs font-semibold text-gray-700">{week}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Left Column - Image and Size */}
          <div className="space-y-6">
            <Card className="border-pink-100 shadow-lg overflow-hidden">
              <div className="relative h-80 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-200/30 to-pink-400/30" />
                <img
                  src={currentWeekData.image}
                  alt={`Tuần ${selectedWeek}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Kích thước thai nhi</p>
                  <div className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
                    {currentWeekData.size}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-pink-100 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-gray-800">
                  <Heart className="w-5 h-5 mr-2 text-pink-500" />
                  Triệu chứng phổ biến
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {currentWeekData.symptoms.map((symptom, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Activity className="w-4 h-4 text-pink-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{symptom}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="md:col-span-2">
            <Card className="border-pink-100 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-800">{currentWeekData.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                      Tổng quan
                    </TabsTrigger>
                    <TabsTrigger value="advice" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                      Lời khuyên
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-6">
                    <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <BookOpen className="w-5 h-5 mr-2 text-pink-500" />
                        Sự phát triển của bé
                      </h3>
                      <p className="text-gray-700 leading-relaxed">{currentWeekData.description}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-pink-50 p-4 rounded-xl text-center border border-pink-100">
                        <div className="text-3xl font-bold text-pink-600 mb-1">{selectedWeek}</div>
                        <div className="text-sm text-gray-600">Tuần thai</div>
                      </div>
                      <div className="bg-pink-50 p-4 rounded-xl text-center border border-pink-100">
                        <div className="text-3xl font-bold text-pink-600 mb-1">{40 - selectedWeek}</div>
                        <div className="text-sm text-gray-600">Tuần còn lại</div>
                      </div>
                      <div className="bg-pink-50 p-4 rounded-xl text-center border border-pink-100">
                        <div className="text-3xl font-bold text-pink-600 mb-1">{Math.round((selectedWeek / 40) * 100)}%</div>
                        <div className="text-sm text-gray-600">Hoàn thành</div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="advice" className="space-y-4">
                    <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Lời khuyên cho tuần này
                      </h3>
                      <ul className="space-y-3">
                        {currentWeekData.advice.map((tip, index) => (
                          <li key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-pink-100 hover:shadow-md transition-shadow">
                            <div className="w-6 h-6 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">
                              {index + 1}
                            </div>
                            <span className="text-gray-700">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
                      <p className="text-sm text-yellow-800">
                        <strong>Lưu ý:</strong> Mọi thông tin chỉ mang tính chất tham khảo. Vui lòng tham khảo ý kiến bác sĩ của bạn.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />

      {/* Register Popup */}
      {!isLoggedIn && (
        <RegisterPopup
          isOpen={showRegisterPopup}
          onClose={() => setShowRegisterPopup(false)}
        />
      )}
    </div>
  );
};

export default PregnancyTracker;